import React, { useEffect, useRef } from 'react';

import classNames from 'classnames';
import { Button, ScrollableContainer } from '@ergeon/core-components';
import { ThemeSwitch } from '@ergeon/help-components';
import { XMarkIcon } from '@ergeon/icons/hero/micro';

import WikiSidebar from 'components/wiki/sidebar/WikiSidebar';

import PhoneLink from './PhoneLink';
import { MobileMenuProps } from './types';

const PANEL_CLASSES = classNames(
  'absolute inset-y-0 left-0 w-80 max-w-full bg-white shadow-xl',
  'focus:outline-hidden dark:bg-gray-900',
);

const useLockedPageScroll = (isLocked: boolean) => {
  useEffect(function keepThePageStillBehindTheMenu() {
    if (!isLocked) {
      return undefined;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isLocked]);
};

const useCloseOnEscape = (isOpen: boolean, onClose: () => void) => {
  useEffect(function closeTheMenuOnEscape() {
    if (!isOpen) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);
};

const MobileMenu: React.FC<MobileMenuProps> = ({ isLoggedIn, isOpen, onClose }) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useLockedPageScroll(isOpen);
  useCloseOnEscape(isOpen, onClose);

  useEffect(function moveFocusIntoTheMenu() {
    if (isOpen) {
      panelRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <div aria-hidden="true" className="absolute inset-0 bg-gray-900/60 dark:bg-gray-900/80" onClick={onClose} />
      <div
        aria-label="Wiki menu"
        aria-modal="true"
        className={PANEL_CLASSES}
        ref={panelRef}
        role="dialog"
        tabIndex={-1}>
        <ScrollableContainer className="px-4" direction="col" gap={6} maskSize={4}>
          <div className="flex items-center justify-between gap-3">
            <p className="heading-5 text-gray-900 dark:text-white">Company Wiki</p>
            <Button
              aria-label="Close menu"
              flavor="regular"
              isSquare
              onClick={onClose}
              taste="boundless">
              <XMarkIcon aria-hidden="true" className="size-6" />
            </Button>
          </div>
          {isLoggedIn && <WikiSidebar />}
          <div className="mt-auto flex flex-col items-start gap-4 border-t border-gray-200 pt-4 dark:border-gray-800">
            <ThemeSwitch tooltipPosition="right" />
            <PhoneLink />
          </div>
        </ScrollableContainer>
      </div>
    </div>
  );
};

export default MobileMenu;
